import { DateTime } from 'luxon';
import {
  getEmptyProductPricing,
  ProductPricing,
  productPricingFromResponse,
  productPricingToUpsertRequest,
} from './ProductPricing';
import { TranslatedText } from '../Translation';

export interface Product {
  id: number;
  name: string;
  nameTranslation: TranslatedText;
  slug: string;
  shortDescription: string;
  shortDescriptionTranslation: TranslatedText;
  description: string;
  descriptionTranslation: TranslatedText;
  basePrice: number;
  pricing: ProductPricing;
  sku?: string;
  barcode?: string;
  hepsiburada_sku?: string;
  quantity: number;
  expiryDate?: Date;
  isActive: boolean;
  isVirtual: boolean;
  hasOptions: boolean;
  productOrder: number;

  brandID?: number;
  taxID?: number;
  categoryIDs: number[];
  filterValueIDs: number[];
  tagIDs: number[];
}

export const getEmptyProduct = (): Product => ({
  id: 0,
  name: '',
  nameTranslation: {},
  slug: '',
  shortDescription: '',
  shortDescriptionTranslation: {},
  description: '',
  descriptionTranslation: {},
  basePrice: 0,
  pricing: getEmptyProductPricing(),
  quantity: 0,
  isActive: false,
  isVirtual: false,
  hasOptions: false,
  productOrder: 1,
  categoryIDs: [],
  filterValueIDs: [],
  tagIDs: [],
});

export const productFromResponse = (responseData: any): Product => ({
  id: responseData.id,
  name: responseData.name,
  nameTranslation: responseData.name_translation ?? {},
  slug: responseData.slug,
  shortDescription: responseData.short_description,
  shortDescriptionTranslation: responseData.short_description_translation ?? {},
  description: responseData.description,
  descriptionTranslation: responseData.description_translation ?? {},
  basePrice: (responseData.base_price ?? 0) / 100,
  pricing: productPricingFromResponse(responseData.pricing),
  sku: responseData.sku,
  barcode: responseData.barcode,
  hepsiburada_sku: responseData.hepsiburada_sku,
  quantity: responseData.quantity,
  expiryDate: responseData.expiry_date
    ? new Date(responseData.expiry_date)
    : undefined,
  isActive: responseData.is_active,
  isVirtual: responseData.is_virtual,
  hasOptions: responseData.has_options,
  productOrder: responseData.product_order,

  brandID: responseData.brand_id,
  taxID: responseData.tax_id,
  categoryIDs: responseData.category_ids ?? [],
  filterValueIDs: responseData.filter_value_ids ?? [],
  tagIDs: responseData.tag_ids ?? [],
});

export const productToUpsertRequest = (product: Product) => ({
  name: product.name,
  name_translation: product.nameTranslation,
  short_description: product.shortDescription,
  short_description_translation: product.shortDescriptionTranslation,
  description: product.description,
  description_translation: product.descriptionTranslation,
  base_price: Math.round(product.basePrice * 100),
  pricing: productPricingToUpsertRequest(product.pricing),
  sku: product.sku?.trim(),
  barcode: product.barcode?.trim(),
  hepsiburada_sku: product.hepsiburada_sku?.trim(),
  quantity: product.quantity,
  expiry_date: product.expiryDate
    ? DateTime.fromJSDate(product.expiryDate)
        .startOf('day')
        .setZone('utc', { keepLocalTime: true })
        .toISO({ suppressMilliseconds: true })
    : undefined,
  is_active: product.isActive,
  is_virtual: product.isVirtual,
  product_order: product.productOrder,

  brand_id: product.brandID,
  tax_id: product.taxID === -1 ? undefined : product.taxID,
  category_ids: product.categoryIDs,
  filter_value_ids: product.filterValueIDs,
  tag_ids: product.tagIDs,
});
